import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { digits, money } from "../lib/plans";
import { ErrorText, Loading, PageHeader } from "../components/ui";
import { BarChart } from "../components/BarChart";
import { StackedBars } from "../components/StackedBars";

interface Metrics {
  conversations: number;
  whatsapp: number;
  ai_only: number;
  ai_messages: number;
  new_ai_replies: number;
  saved_replies: number;
  tokens_per_ai_reply?: number;
  automation_messages: number;
  after_hours_messages: number;
  leads: number;
  deals_created: number;
  deals_won: number;
  deals_lost: number;
  won_minor: number;
  all_won_minor: number;
  other_currency_won: number;
  ai_first_token_p50_ms: number | null;
  staff_reply_p50_seconds: number | null;
}

interface ROI {
  days: number;
  bucket: "day" | "week" | "month";
  currency: string;
  timezone: string;
  current: Metrics;
  previous: Metrics;
  pipeline: { open: number; open_minor: number; weighted_minor: number };
  series: { start: string; leads: number; conversations: number; won_minor: number; new_ai_replies: number; saved_replies: number; automation_messages: number }[];
  awaiting: { id: string; title: string; contact_name: string | null; amount_minor: number | null; currency: string; stage_name: string; since: string; overdue: boolean }[];
  minutes_per_message: number;
}

const periods = [7, 30, 90, 365];

// Fixed colours per kind of reply (checked for colour-blind separation on the dark panel).
const replySeries = [
  { key: "saved", label: "Saved answers", color: "#1F9D68" },
  { key: "ai", label: "AI replies", color: "#4C8DFF" },
  { key: "automation", label: "Automations", color: "#C8762A" },
];

function duration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} days`;
}

/** Change vs the previous period, e.g. "▲ 25%". */
function Change({ now, before }: { now: number; before: number }) {
  if (before === 0 && now === 0) return null;
  if (before === 0) return <span className="change up">new</span>;
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return <span className="change">same as before</span>;
  return <span className={`change ${pct > 0 ? "up" : "down"}`}>{pct > 0 ? "▲" : "▼"} {Math.abs(pct)}%</span>;
}

function Tile({ label, value, sub, change }: { label: string; value: string; sub?: React.ReactNode; change?: React.ReactNode }) {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <b className="kpi-value">{value}</b>
      <span className="kpi-sub muted small">{change} {sub}</span>
    </div>
  );
}

export default function Results() {
  const { base, orgId, org } = useOrg();
  const [days, setDays] = useState(30);
  const roi = useQuery({ queryKey: [orgId, "roi", days], queryFn: () => api<ROI>(`${base}/reports/roi?days=${days}`) });
  if (roi.isLoading) return <Loading rows={6} />;
  if (roi.error) return <ErrorText error={roi.error} />;
  const r = roi.data!;
  const c = r.current;
  const p = r.previous;
  const cur = (v: number) => money(v, r.currency);
  const short = (major: number) => `${major.toLocaleString("en-US", { maximumFractionDigits: digits(r.currency) })} ${r.currency}`;
  const hoursSaved = (c.ai_messages * r.minutes_per_message) / 60;
  const closed = c.deals_won + c.deals_lost;
  const label = (iso: string, full: boolean) => {
    const d = new Date(iso);
    const opts: Intl.DateTimeFormatOptions = r.bucket === "month" ? { month: "short", year: full ? "numeric" : undefined } : { day: "numeric", month: "short" };
    const s = d.toLocaleDateString("en-GB", { ...opts, timeZone: r.timezone });
    return full && r.bucket === "week" ? `Week of ${s}` : s;
  };
  const auto = c.saved_replies + c.automation_messages;
  const noNewAI = auto + c.new_ai_replies > 0 ? Math.round((auto / (auto + c.new_ai_replies)) * 100) : null;
  const periodName = days === 365 ? "the last 12 months" : `the last ${days} days`;

  return (
    <>
      <PageHeader
        title="Results"
        actions={
          <div className="segmented" role="group" aria-label="Period">
            {periods.map((d) => (
              <button key={d} className={d === days ? "" : "secondary"} onClick={() => setDays(d)}>{d === 365 ? "12 months" : `${d} days`}</button>
            ))}
          </div>
        }
      />
      <p className="results-summary">
        In {periodName}, your AI answered <b>{c.conversations.toLocaleString()}</b> conversation{c.conversations === 1 ? "" : "s"}, brought in{" "}
        <b>{c.leads.toLocaleString()}</b> lead{c.leads === 1 ? "" : "s"} and helped win <b>{cur(c.won_minor)}</b> for {org?.display_name}.
      </p>

      <div className="kpi-grid">
        <Tile label="Revenue won with AI" value={cur(c.won_minor)} change={<Change now={c.won_minor} before={p.won_minor} />}
          sub={c.all_won_minor > 0 ? `${Math.round((c.won_minor / c.all_won_minor) * 100)}% of ${cur(c.all_won_minor)} won in total` : undefined} />
        <Tile label="Leads brought in" value={c.leads.toLocaleString()} change={<Change now={c.leads} before={p.leads} />} />
        <Tile label="Conversations answered" value={c.conversations.toLocaleString()} change={<Change now={c.conversations} before={p.conversations} />}
          sub={c.conversations > 0 ? `${Math.round((c.ai_only / c.conversations) * 100)}% without staff` : undefined} />
        <Tile label="Deals won" value={`${c.deals_won}`} change={<Change now={c.deals_won} before={p.deals_won} />}
          sub={closed > 0 ? `${Math.round((c.deals_won / closed) * 100)}% win rate` : `${c.deals_created} opened`} />
        <Tile label="Open deals in progress" value={cur(r.pipeline.open_minor)} sub={`${r.pipeline.open} deal${r.pipeline.open === 1 ? "" : "s"} · likely ${cur(r.pipeline.weighted_minor)}`} />
        <Tile label="Answered after hours" value={c.after_hours_messages.toLocaleString()} change={<Change now={c.after_hours_messages} before={p.after_hours_messages} />} sub="replies before 8am, after 6pm or on Fridays" />
        <Tile label="Staff time saved" value={`≈ ${hoursSaved < 10 ? hoursSaved.toFixed(1) : Math.round(hoursSaved)} h`} sub={`${c.ai_messages.toLocaleString()} AI replies × ${r.minutes_per_message} min`} />
        <Tile label="Response time" value={c.ai_first_token_p50_ms ? `${(c.ai_first_token_p50_ms / 1000).toFixed(1)} sec` : "—"} sub={`AI, typical · staff: ${duration(c.staff_reply_p50_seconds)}`} />
      </div>

      <section className="card replies-card">
        <div className="replies-head">
          <div>
            <h2>How replies were made</h2>
            <p className="muted small">Saved answers and automations reply without asking the AI to write anything new. That saves cost and energy, and every saved answer you add raises this share.</p>
          </div>
          <div className="replies-share">
            <b>{noNewAI === null ? "—" : `${noNewAI}%`}</b>
            <span className="muted small">sent without a new AI reply</span>
          </div>
        </div>
        <div className="replies-kpis">
          <div><span className="kchip saved">Saved answer</span><b>{c.saved_replies.toLocaleString()}</b><Change now={c.saved_replies} before={p.saved_replies} /></div>
          <div><span className="kchip ai">AI reply</span><b>{c.new_ai_replies.toLocaleString()}</b><Change now={c.new_ai_replies} before={p.new_ai_replies} /></div>
          <div><span className="kchip auto">Automation</span><b>{c.automation_messages.toLocaleString()}</b><Change now={c.automation_messages} before={p.automation_messages} /></div>
        </div>
        {c.saved_replies > 0 && (c.tokens_per_ai_reply ?? 0) > 0 && (
          <p className="small saved-impact">
            Saved answers took the place of <b>{c.saved_replies.toLocaleString()}</b> AI {c.saved_replies === 1 ? "reply" : "replies"}: about <b>{(c.saved_replies * c.tokens_per_ai_reply!).toLocaleString()}</b> tokens of AI work that didn't need doing (one AI reply here uses about {c.tokens_per_ai_reply!.toLocaleString()}).
          </p>
        )}
        <StackedBars title="Replies over time" series={replySeries}
          bars={r.series.map((s) => ({ label: label(s.start, false), full: label(s.start, true), values: { saved: s.saved_replies, ai: s.new_ai_replies, automation: s.automation_messages } }))} />
      </section>

      <div className="chart-grid-2">
        <section className="card">
          <BarChart title={`Revenue won with AI (${r.currency})`} format={short} bars={r.series.map((s) => ({ label: label(s.start, false), full: label(s.start, true), value: s.won_minor / 10 ** digits(r.currency) }))} />
        </section>
        <section className="card">
          <BarChart title="Leads brought in" format={(v) => Math.round(v).toLocaleString()} bars={r.series.map((s) => ({ label: label(s.start, false), full: label(s.start, true), value: s.leads }))} />
        </section>
      </div>

      <section className="card">
        <h2>Deals waiting for an outcome</h2>
        {r.awaiting.length === 0 ? (
          <p className="muted small">Nothing waiting. Deals past their expected close date or stuck in a stage for two weeks show up here.</p>
        ) : (
          <>
            <p className="muted small">Mark these won or lost so your results stay accurate.</p>
            <ul className="list">
              {r.awaiting.map((d) => (
                <li key={d.id}>
                  <Link to={`/o/${orgId}/deals/${d.id}`}>{d.title}</Link>
                  <span className="muted small"> · {d.contact_name ?? "no contact"} · {d.stage_name} · {money(d.amount_minor, d.currency)}</span>
                  {d.overdue && <span className="tag warn-tag">past expected date</span>}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <p className="muted small">
        How this is counted: a deal counts as won with AI when the AI created it, or talked with that customer before the deal was created. Revenue is what your team marks as won
        {c.other_currency_won > 0 && ` (${c.other_currency_won} deal${c.other_currency_won === 1 ? "" : "s"} in other currencies not included)`}. Leads are customers the AI captured or who first
        reached you through the AI on your website or WhatsApp. Times are in {r.timezone}.
      </p>
    </>
  );
}
